import { ChangeEvent, FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Stack } from "@mui/system";
import { Grid, Tab } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";

import {
	faCheck,
	faCircleCheck,
	faTriangleExclamation,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import { Icon, Modal } from "@/components";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useSaveHubspotApiKeyMutation,
	useDeleteHubspotApiKeyMutation,
	useLazyValidateHubspotApiKeyQuery,
	useLazyGetOAuthAuthorizeUrlQuery,
	useGetOAuthStatusQuery,
	useDisconnectOAuthMutation,
} from "@/pages/Private/redux/hubspot/hubspot.api";

import { CompanyType } from "../schema/company";
import { HubspotFieldMappingModal } from "./HubspotFieldMappingModal";
import { HubspotDealConfigTab } from "./HubspotDealConfigTab";
import { HubspotLeadConfigTab } from "./HubspotLeadConfigTab";
import { HubspotUserMappingTab } from "./HubspotUserMappingTab";
import { HubspotSettingsTab } from "./HubspotSettingsTab";
import { HubspotSyncLogsTab } from "./HubspotSyncLogsTab";

type ApiKeyHubspotProps = {
	company?: CompanyType;
};

enum HubspotTabs {
	CONNECTION = "CONNECTION",
	SETTINGS = "SETTINGS",
	DEAL_CONFIGURATION = "DEAL_CONFIGURATION",
	LEAD_CONFIGURATION = "LEAD_CONFIGURATION",
	USER_MAPPING = "USER_MAPPING",
	SYNC_LOGS = "SYNC_LOGS",
}

export const ApiKeyHubspot: FunctionComponent<ApiKeyHubspotProps> = ({ company }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.${key}`);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [searchParams, setSearchParams] = useSearchParams();
	const currentTab = (searchParams.get("hubspotTab") as HubspotTabs) || HubspotTabs.CONNECTION;

	const [canEdit, setCanEdit] = useState<boolean>(false);
	const [apiKey, setApiKey] = useState<string>(
		company?.hasHubspotApiKey ? "******************" : ""
	);
	const [isFieldMappingOpen, setIsFieldMappingOpen] = useState<boolean>(false);
	const [validationStatus, setValidationStatus] = useState<
		"idle" | "testing" | "valid" | "invalid"
	>("idle");
	const [showApiKeySection, setShowApiKeySection] = useState<boolean>(false);
	const [showDisconnectModal, setShowDisconnectModal] = useState<boolean>(false);

	const [saveApiKey, { isLoading: isSaving }] = useSaveHubspotApiKeyMutation();
	const [deleteApiKey, { isLoading: isDeleting }] = useDeleteHubspotApiKeyMutation();
	const [validateApiKey, { isLoading: isValidating }] = useLazyValidateHubspotApiKeyQuery();

	// OAuth hooks
	const [getOAuthAuthorizeUrl, { isLoading: isLoadingOAuthUrl }] =
		useLazyGetOAuthAuthorizeUrlQuery();
	const { data: oauthStatus } = useGetOAuthStatusQuery();
	const [disconnectOAuth, { isLoading: isDisconnecting }] = useDisconnectOAuthMutation();

	const isConnectedOAuth = oauthStatus?.connected ?? false;
	const isConnectedApiKey = company?.hasHubspotApiKey ?? false;
	const isConnected = isConnectedOAuth || isConnectedApiKey;

	const handleTabsChange = useCallback(
		(_: ChangeEvent<{}>, value: HubspotTabs): void => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					next.set("hubspotTab", value);

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const handleSave = async () => {
		const cleanApiKey = apiKey.replaceAll("*", "");

		if (cleanApiKey.length > 10) {
			try {
				await saveApiKey({ apiKey: cleanApiKey }).unwrap();
				setCanEdit(false);
				setValidationStatus("idle");
			} catch (error) {
				console.error("Failed to save HubSpot API key:", error);
			}
		}
	};

	const handleDisconnectApiKey = async () => {
		try {
			await deleteApiKey().unwrap();
			setApiKey("");
			setValidationStatus("idle");
		} catch (error) {
			console.error("Failed to delete HubSpot API key:", error);
		}
	};

	const handleConnectOAuth = async () => {
		try {
			const result = await getOAuthAuthorizeUrl().unwrap();

			window.location.href = result.url;
		} catch (error) {
			handleOpenSnackbar(ts("hubspot.oauth.connectError"));
		}
	};

	const handleDisconnectOAuth = async () => {
		try {
			await disconnectOAuth().unwrap();
			setShowDisconnectModal(false);
		} catch (error) {
			console.error("Failed to disconnect OAuth:", error);
		}
	};

	const handleTestConnection = async () => {
		setValidationStatus("testing");
		try {
			const result = await validateApiKey().unwrap();

			setValidationStatus(result.valid ? "valid" : "invalid");
		} catch (error) {
			setValidationStatus("invalid");
		}
	};

	const handleCancel = () => {
		setCanEdit(false);
		setApiKey(company?.hasHubspotApiKey ? "******************" : "");
	};

	// OAuth connection status card (State 2)
	const oauthConnectionContent = (
		<div className="flex flex-col gap-4">
			<div className="flex items-center gap-3 p-4 rounded-lg border border-status-success bg-green-50">
				<Icon className="w-5 h-5 text-status-success" icon={faCircleCheck} />
				<div className="flex flex-col">
					<span className="text-sm font-semibold text-status-success">
						{ts("hubspot.oauth.connectedViaOAuth")}
					</span>
					{oauthStatus?.hubspotPortalId && (
						<span className="text-xs text-gray-500">
							{ts("hubspot.oauth.portalId")}: {oauthStatus.hubspotPortalId}
						</span>
					)}
					<span className="text-xs text-gray-400">{ts("hubspot.oauth.tokenRefresh")}</span>
				</div>
			</div>
			{oauthStatus?.needsReauthorization && (
				<div
					className="flex items-center gap-3 p-4 rounded-lg border border-status-warning bg-status-warning-light"
					data-testid="hubspot-reauth-banner"
				>
					<Icon className="w-5 h-5 text-status-warning" icon={faTriangleExclamation} />
					<div className="flex flex-1 flex-col">
						<span className="text-sm font-semibold text-status-warning">
							{ts("hubspot.oauth.reauthTitle")}
						</span>
						<span className="text-xs text-gray-600">{ts("hubspot.oauth.reauthDescription")}</span>
					</div>
					<Button
						color={ButtonColor.ACTION}
						isLoading={isLoadingOAuthUrl}
						size={ButtonSize.S}
						title={ts("hubspot.oauth.reconnect")}
						onClick={handleConnectOAuth}
					/>
				</div>
			)}
			<div className="flex flex-row gap-2">
				<div className="flex flex-wrap gap-2 w-fit">
					<Button
						color={ButtonColor.DANGER_SECONDARY}
						isLoading={isDisconnecting}
						size={ButtonSize.S}
						title={ts("hubspot.disconnect")}
						onClick={() => setShowDisconnectModal(true)}
					/>
				</div>

				<div className="w-fit">
					<Button
						color={ButtonColor.ACTION}
						size={ButtonSize.S}
						title={ts("hubspot.configureFieldMapping")}
						onClick={() => setIsFieldMappingOpen(true)}
					/>
				</div>
			</div>
		</div>
	);

	// API Key connection content (legacy — State 3, or standalone)
	const apiKeyConnectionContent = (
		<Grid size={{ md: 8, xs: 12 }}>
			<Stack spacing={3}>
				<div className="flex flex-col">
					<InputField
						handleChange={(e) => setApiKey(e.target.value)}
						isDisabled={!canEdit && isConnectedApiKey}
						name="hubspotApiKey"
						placeholder={ts("hubspotApiKey")}
						size={ButtonSize.S}
						value={apiKey}
					/>

					<div className="flex flex-wrap gap-2 mt-2">
						{!isConnectedApiKey && !canEdit && (
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								title={ts("hubspot.connect")}
								onClick={() => setCanEdit(true)}
							/>
						)}

						{!isConnectedApiKey && canEdit && (
							<>
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									size={ButtonSize.S}
									title={t("basics.cancel")}
									onClick={handleCancel}
								/>
								<Button
									isLoading={isSaving}
									size={ButtonSize.S}
									title={t("basics.save")}
									onClick={handleSave}
								/>
							</>
						)}

						{isConnectedApiKey && !canEdit && (
							<>
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									size={ButtonSize.S}
									title={t("basics.edit")}
									onClick={() => setCanEdit(true)}
								/>
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									isLoading={isValidating}
									size={ButtonSize.S}
									title={ts("hubspot.testConnection")}
									onClick={handleTestConnection}
								/>
								<Button
									color={ButtonColor.DANGER_SECONDARY}
									isLoading={isDeleting}
									size={ButtonSize.S}
									title={ts("hubspot.disconnect")}
									onClick={handleDisconnectApiKey}
								/>
							</>
						)}

						{isConnectedApiKey && canEdit && (
							<>
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									size={ButtonSize.S}
									title={t("basics.cancel")}
									onClick={handleCancel}
								/>
								<Button
									isLoading={isSaving}
									size={ButtonSize.S}
									title={t("basics.save")}
									onClick={handleSave}
								/>
							</>
						)}
					</div>

					{validationStatus !== "idle" && (
						<div className="mt-2">
							{validationStatus === "testing" && (
								<span className="text-sm text-gray-500">{ts("hubspot.validation.testing")}</span>
							)}
							{validationStatus === "valid" && (
								<span className="text-sm text-status-success flex items-center gap-1">
									<Icon className="w-4 h-4" icon={faCheck} />
									{ts("hubspot.validation.valid")}
								</span>
							)}
							{validationStatus === "invalid" && (
								<span className="text-sm text-status-error flex items-center gap-1">
									<Icon className="w-4 h-4" icon={faXmark} />
									{ts("hubspot.validation.invalid")}
								</span>
							)}
						</div>
					)}
				</div>
			</Stack>
		</Grid>
	);

	// Connection tab content — shows OAuth status + optional API key section
	const connectionContent = (
		<div className="flex flex-col gap-4">
			{isConnectedOAuth ? (
				oauthConnectionContent
			) : (
				<>
					{/* OAuth connect button */}
					<div className="flex flex-col gap-2 w-fit">
						<Button
							className="max-w-[250px]"
							color={ButtonColor.ACTION}
							isLoading={isLoadingOAuthUrl}
							size={ButtonSize.S}
							title={
								isConnectedApiKey
									? ts("hubspot.oauth.upgradeToOAuth")
									: ts("hubspot.oauth.connectWithHubspot")
							}
							onClick={handleConnectOAuth}
						/>
					</div>

					{/* API Key section */}
					{isConnectedApiKey ? (
						<>
							<div className="flex items-center gap-2 mt-2">
								<span className="text-xs text-gray-400">
									{ts("hubspot.oauth.connectedViaApiKey")}
								</span>
							</div>
							{apiKeyConnectionContent}
							<div className="mt-2 pt-4 border-t border-gray-200 w-fit">
								<Button
									color={ButtonColor.ACTION}
									size={ButtonSize.S}
									title={ts("hubspot.configureFieldMapping")}
									onClick={() => setIsFieldMappingOpen(true)}
								/>
							</div>
						</>
					) : (
						<div className="mt-2 w-fit">
							<button
								className="text-xs text-gray-400 underline cursor-pointer bg-transparent border-none p-0"
								type="button"
								onClick={() => setShowApiKeySection(!showApiKeySection)}
							>
								{ts("hubspot.oauth.advancedApiKey")}
							</button>
							{showApiKeySection && <div className="mt-3">{apiKeyConnectionContent}</div>}
						</div>
					)}
				</>
			)}
		</div>
	);

	return (
		<>
			<div className="p-4 rounded-xl border border-gray-200 bg-brand-white">
				<div className="flex items-center justify-between mb-4">
					<div className="text-smd">{ts("hubspot.title")}</div>
					{isConnected && (
						<div className="flex items-center gap-2">
							<div className="flex items-center gap-1 text-sm text-status-success">
								<Icon className="w-4 h-4" icon={faCheck} />
								{ts("hubspot.connected")}
							</div>
						</div>
					)}
					{!isConnected && (
						<div className="flex items-center gap-1 text-sm text-gray-500">
							<Icon className="w-4 h-4" icon={faXmark} />
							{ts("hubspot.notConnected")}
						</div>
					)}
				</div>

				{isConnected ? (
					<TabContext value={currentTab}>
						<TabList onChange={handleTabsChange}>
							<Tab label={ts("hubspot.tabs.connection")} value={HubspotTabs.CONNECTION} />
							<Tab label={ts("hubspot.tabs.settings")} value={HubspotTabs.SETTINGS} />
							<Tab
								label={ts("hubspot.tabs.dealConfiguration")}
								value={HubspotTabs.DEAL_CONFIGURATION}
							/>
							<Tab
								label={ts("hubspot.tabs.leadConfiguration")}
								value={HubspotTabs.LEAD_CONFIGURATION}
							/>
							<Tab label={ts("hubspot.tabs.userMapping")} value={HubspotTabs.USER_MAPPING} />
							<Tab label={ts("hubspot.tabs.syncLogs")} value={HubspotTabs.SYNC_LOGS} />
						</TabList>
						<TabPanel sx={{ px: 0 }} value={HubspotTabs.CONNECTION}>
							{connectionContent}
						</TabPanel>
						<TabPanel sx={{ px: 0 }} value={HubspotTabs.SETTINGS}>
							<HubspotSettingsTab isConnected={isConnected} />
						</TabPanel>
						<TabPanel sx={{ px: 0 }} value={HubspotTabs.DEAL_CONFIGURATION}>
							<HubspotDealConfigTab />
						</TabPanel>
						<TabPanel sx={{ px: 0 }} value={HubspotTabs.LEAD_CONFIGURATION}>
							<HubspotLeadConfigTab />
						</TabPanel>
						<TabPanel sx={{ px: 0 }} value={HubspotTabs.USER_MAPPING}>
							<HubspotUserMappingTab />
						</TabPanel>
						<TabPanel sx={{ px: 0 }} value={HubspotTabs.SYNC_LOGS}>
							<HubspotSyncLogsTab />
						</TabPanel>
					</TabContext>
				) : (
					connectionContent
				)}
			</div>

			{isFieldMappingOpen && (
				<HubspotFieldMappingModal
					isOpen={isFieldMappingOpen}
					onClose={() => setIsFieldMappingOpen(false)}
				/>
			)}

			{showDisconnectModal && (
				<Modal
					handleClose={() => setShowDisconnectModal(false)}
					handleSave={handleDisconnectOAuth}
					isLoading={isDisconnecting}
					isOpened={showDisconnectModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={ts("hubspot.disconnect")}
					title={ts("hubspot.oauth.disconnectTitle")}
				>
					<div className="text-sm">{ts("hubspot.oauth.disconnectBody")}</div>
				</Modal>
			)}

			{renderMessageSnackbar()}
		</>
	);
};
